import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  CircleDotDashed,
  XCircle,
} from 'lucide-react';

/**
 * AgentPlanThinking — hierarchical task breakdown shown during agent thinking/executing.
 *
 * Props:
 *   tasks         — array from task_breakdown SSE event:
 *                   [{id, title, description, subtasks?: [{id, title, description, tools?: []}]}]
 *   taskStatuses  — map of id → 'pending'|'in_progress'|'completed'|'failed'
 */
function AgentPlanThinking({ tasks = [], taskStatuses = {} }) {
  const [expandedTasks, setExpandedTasks] = useState(() => {
    // Auto-expand the first task
    return tasks.length > 0 ? [tasks[0].id] : [];
  });
  const [expandedSubtasks, setExpandedSubtasks] = useState({});

  if (!tasks || tasks.length === 0) return null;

  const toggleTask = (taskId) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleSubtask = (key) => {
    setExpandedSubtasks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatus = (id) => taskStatuses[id] || 'pending';

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 6 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.25, ease: [0.2, 0.65, 0.3, 0.9] },
    },
  };

  const subtaskListVariants = {
    hidden: { opacity: 0, height: 0, overflow: 'hidden' },
    visible: {
      height: 'auto',
      opacity: 1,
      overflow: 'visible',
      transition: {
        duration: 0.22,
        staggerChildren: 0.04,
        when: 'beforeChildren',
        ease: [0.2, 0.65, 0.3, 0.9],
      },
    },
    exit: {
      height: 0,
      opacity: 0,
      overflow: 'hidden',
      transition: { duration: 0.18, ease: [0.2, 0.65, 0.3, 0.9] },
    },
  };

  const subtaskVariants = {
    hidden: { opacity: 0, x: -8 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: 'spring', stiffness: 500, damping: 28 },
    },
    exit: { opacity: 0, x: -8, transition: { duration: 0.12 } },
  };

  const subtaskDetailVariants = {
    hidden: { opacity: 0, height: 0, overflow: 'hidden' },
    visible: {
      opacity: 1,
      height: 'auto',
      overflow: 'visible',
      transition: { duration: 0.2, ease: [0.2, 0.65, 0.3, 0.9] },
    },
  };

  const iconSwitchVariants = {
    initial: { opacity: 0, scale: 0.7, rotate: -12 },
    animate: { opacity: 1, scale: 1, rotate: 0, transition: { duration: 0.18, ease: [0.2, 0.65, 0.3, 0.9] } },
    exit: { opacity: 0, scale: 0.7, rotate: 12, transition: { duration: 0.12 } },
  };

  return (
    <motion.div
      className="my-2 rounded-lg border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="px-3 py-2">
        <ul className="space-y-0.5">
          {tasks.map((task, index) => {
            const status = getStatus(task.id);
            const isExpanded = expandedTasks.includes(task.id);
            const isCompleted = status === 'completed';

            return (
              <motion.li
                key={task.id}
                className={index !== 0 ? 'pt-1.5 mt-1 border-t border-white/5' : ''}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0, transition: { delay: index * 0.05 } }}
              >
                {/* Task row */}
                <div
                  className="group flex items-center px-2 py-1.5 rounded-md cursor-pointer hover:bg-white/5 transition-colors duration-150"
                  onClick={() => toggleTask(task.id)}
                >
                  {/* Status icon */}
                  <div className="mr-2 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={status}
                        variants={iconSwitchVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                      >
                        <StatusIcon status={status} size="task" />
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Title + status badge */}
                  <div className="flex flex-1 min-w-0 items-center justify-between gap-2">
                    <span
                      className={`text-sm leading-5 truncate ${
                        isCompleted ? 'line-through text-white/30' : 'text-white/80'
                      }`}
                    >
                      {task.title}
                    </span>
                    <StatusBadge status={status} />
                  </div>
                </div>

                {/* Subtasks */}
                <AnimatePresence mode="wait">
                  {isExpanded && task.subtasks && task.subtasks.length > 0 && (
                    <motion.div
                      className="relative overflow-hidden"
                      variants={subtaskListVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      {/* Vertical connector line */}
                      <div
                        className="absolute top-0 bottom-0 bg-white/15"
                        style={{ left: '20px', width: '1px' }}
                      />
                      <ul className="mt-1 mb-1.5 ml-3 mr-2 space-y-0.5">
                        {task.subtasks.map((subtask) => {
                          const subStatus = getStatus(subtask.id);
                          const subKey = `${task.id}-${subtask.id}`;
                          const isSubExpanded = expandedSubtasks[subKey];
                          const isSubCompleted = subStatus === 'completed';

                          return (
                            <motion.li
                              key={subtask.id}
                              className="flex flex-col py-0.5 pl-6"
                              variants={subtaskVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                            >
                              <div
                                className="flex items-center rounded-md p-1 cursor-pointer hover:bg-white/5 transition-colors duration-150"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSubtask(subKey);
                                }}
                              >
                                {/* Subtask icon */}
                                <div className="mr-2 flex-shrink-0 w-3.5 h-3.5 flex items-center justify-center">
                                  <AnimatePresence mode="wait">
                                    <motion.div
                                      key={subStatus}
                                      variants={iconSwitchVariants}
                                      initial="initial"
                                      animate="animate"
                                      exit="exit"
                                    >
                                      <StatusIcon status={subStatus} size="subtask" />
                                    </motion.div>
                                  </AnimatePresence>
                                </div>
                                <span
                                  className={`text-xs leading-4 ${
                                    isSubCompleted
                                      ? 'line-through text-white/25'
                                      : 'text-white/60'
                                  }`}
                                >
                                  {subtask.title}
                                </span>
                              </div>

                              {/* Subtask detail (tools + description on expand) */}
                              <AnimatePresence mode="wait">
                                {isSubExpanded && (
                                  <motion.div
                                    className="ml-1.5 pl-5 border-l border-dashed border-white/10 mt-0.5 overflow-hidden"
                                    variants={subtaskDetailVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                  >
                                    <p className="text-[11px] text-white/40 py-1 leading-snug">
                                      {subtask.description}
                                    </p>
                                    {subtask.tools && subtask.tools.length > 0 && (
                                      <div className="flex flex-wrap items-center gap-1 mb-1.5">
                                        <span className="text-[10px] text-white/30 font-medium">
                                          tools:
                                        </span>
                                        {subtask.tools.map((tool, i) => (
                                          <motion.span
                                            key={i}
                                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.06] text-white/40 border border-white/10"
                                            initial={{ opacity: 0, y: -3 }}
                                            animate={{
                                              opacity: 1,
                                              y: 0,
                                              transition: { delay: i * 0.04 },
                                            }}
                                          >
                                            {tool}
                                          </motion.span>
                                        ))}
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.li>
                          );
                        })}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
}

/* Status icon — adapts size for task vs subtask rows */
function StatusIcon({ status, size = 'task' }) {
  const cls = size === 'task' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  if (status === 'completed') return <CheckCircle2 className={`${cls} text-emerald-400`} />;
  if (status === 'in_progress') return <CircleDotDashed className={`${cls} text-blue-400 animate-pulse`} />;
  if (status === 'failed') return <XCircle className={`${cls} text-red-400`} />;
  if (status === 'need_help') return <AlertCircle className={`${cls} text-amber-400`} />;
  // pending
  return <Circle className={`${cls} text-white/20`} />;
}

/* Inline status badge */
function StatusBadge({ status }) {
  const map = {
    completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    failed: 'bg-red-500/10 text-red-400 border-red-500/20',
    need_help: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    pending: 'bg-white/5 text-white/25 border-white/10',
  };
  const label = {
    completed: 'done',
    in_progress: 'running',
    failed: 'failed',
    need_help: 'blocked',
    pending: 'pending',
  };
  const classes = map[status] || map.pending;
  return (
    <span
      className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${classes}`}
    >
      {label[status] || status}
    </span>
  );
}

export default AgentPlanThinking;
